import { useCallback, useEffect, useRef, useState } from "react";

const CLOSE_DELAY_MS = 80;

export function useHoverMenuOpen(enabled = true) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const leaveTimerRef = useRef(null);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearLeaveTimer();
    setOpen(false);
  }, [clearLeaveTimer]);

  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

  useEffect(() => {
    if (!open || !enabled) return undefined;

    const onPointerDown = (event) => {
      const wrap = wrapRef.current;
      if (wrap && !wrap.contains(event.target)) close();
    };

    const onPointerMove = (event) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const underPointer = document.elementFromPoint(event.clientX, event.clientY);
      if (underPointer && wrap.contains(underPointer)) {
        clearLeaveTimer();
        return;
      }
      if (!leaveTimerRef.current) {
        leaveTimerRef.current = setTimeout(close, CLOSE_DELAY_MS);
      }
    };

    const onWindowBlur = () => close();

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointermove", onPointerMove);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [open, enabled, close, clearLeaveTimer]);

  const onMouseEnter = useCallback(() => {
    if (!enabled) return;
    clearLeaveTimer();
    setOpen(true);
    requestAnimationFrame(() => {
      const wrap = wrapRef.current;
      if (wrap && !wrap.matches(":hover")) setOpen(false);
    });
  }, [enabled, clearLeaveTimer]);

  const onMouseLeave = useCallback(() => {
    clearLeaveTimer();
    leaveTimerRef.current = setTimeout(close, CLOSE_DELAY_MS);
  }, [clearLeaveTimer, close]);

  return { open, setOpen, wrapRef, onMouseEnter, onMouseLeave };
}
